import React, { useMemo, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from '../../../../components/ui/Button';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toggleDialog } from '../../../../redux/Slices/Global/globalSlice';
import 'chart.js/auto';
import { Chart } from 'primereact/chart';

// Read a CSS custom property from :root. Chart.js can't consume var(--*)
// directly, so we resolve the token at render time. Fallback covers SSR
// + the small window between bootstrapTheme() and the first paint.
const cssVar = (name, fallback) => {
  if (typeof window === 'undefined') return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
};

function AnalyticsDetailDialog({ dataItem }) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { openDialog } = useSelector((state) => state.global);
    const visible = openDialog.analyticsDetailDialog;

    const [chartReady, setChartReady] = useState(false); // render chart only after dialog is shown

    const onClose = () => {
        setChartReady(false);
        dispatch(toggleDialog('analyticsDetailDialog'));
    };

    const campaign = dataItem || {};
    const ages = Array.isArray(campaign.ages) ? campaign.ages : [];
    const male = Number(campaign?.gender?.MALE ?? 0);
    const female = Number(campaign?.gender?.FEMALE ?? 0);
    const totalGender = male + female;
    const malePct = totalGender ? Math.round((male / totalGender) * 100) : 0;
    const femalePct = totalGender ? 100 - malePct : 0;

    // ---------- Age bucketing: 20–29, 30–39, ... ----------
    const { bucketLabels, bucketCounts, maxBucket } = useMemo(() => {
        const sorted = [...ages]
            .filter(a => a && a.age != null)
            .map(a => ({ age: Number(a.age), count: Number(a.count || 0) }))
            .sort((a, b) => a.age - b.age);

        if (sorted.length === 0) return { bucketLabels: [], bucketCounts: [], maxBucket: 0 };

        const minAge = Math.floor(sorted[0].age / 10) * 10;
        const maxAge = Math.ceil(sorted[sorted.length - 1].age / 10) * 10;

        const labels = [];
        const counts = [];
        const map = new Map();

        for (let start = minAge; start < maxAge; start += 10) {
            const end = start + 9;
            const label = `${start}–${end}`;
            labels.push(label);
            map.set(label, 0);
        }

        for (const { age, count } of sorted) {
            const start = Math.floor(age / 10) * 10;
            const end = start + 9;
            const key = `${start}–${end}`;
            if (map.has(key)) map.set(key, map.get(key) + count);
        }

        for (const l of labels) counts.push(map.get(l) || 0);

        return { bucketLabels: labels, bucketCounts: counts, maxBucket: counts.length ? Math.max(...counts) : 0 };
    }, [ages]);

    const niceCeil = (n) => {
        if (n <= 6) return 6;
        // round up to a "nice" number (multiple of 5 or 10)
        const pow = Math.pow(10, Math.floor(Math.log10(n)));
        const unit = pow / 2; // 5, 50, ...
        return Math.ceil(n / unit) * unit;
    };

    const barData = {
        labels: bucketLabels,
        datasets: [
            {
                label: t('devices.setup_analytics_views'),
                data: bucketCounts,
                backgroundColor: 'rgba(59,130,246,0.9)',
                borderColor: 'rgba(59,130,246,1)',
                borderWidth: 1,
                borderRadius: 8,
                borderSkipped: false,
                categoryPercentage: 0.7,
                barPercentage: 0.9,
            },
        ],
    };

    const barOptions = {
        // prevent the initial "short then expand"
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        responsiveAnimationDuration: 0,
        resizeDelay: 0,

        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    title: (items) => items[0]?.label || '',
                    label: (ctx) => `${ctx.raw} view${ctx.raw === 1 ? '' : 's'}`,
                },
            },
        },
        layout: { padding: { top: 8, right: 8, left: 8, bottom: 0 } },
        scales: {
            x: {
                grid: { display: false },
                ticks: { color: cssVar('--pp-text-secondary', '#6b7280'), font: { size: 11 }, maxRotation: 0, autoSkip: true },
                border: { color: cssVar('--pp-border', '#cbd5e1') },
            },
            y: {
                beginAtZero: true,
                suggestedMax: niceCeil(maxBucket),
                ticks: {
                    stepSize: maxBucket <= 10 ? 1 : undefined,
                    precision: 0,
                    color: cssVar('--pp-text-secondary', '#6b7280'),
                    font: { size: 11 },
                },
                grid: { color: cssVar('--pp-border', '#e5e7eb') },
                border: { color: cssVar('--pp-border', '#cbd5e1') },
            },
        },
    };

    const footer = (
        <Button label={t('buttons.close')} icon="pi pi-times" className="p-button-text" onClick={onClose} />
    );

    const isEmpty =
        (!campaign?.views && campaign?.views !== 0) &&
        ages.length === 0 &&
        totalGender === 0;

    return (
        <Dialog
            visible={visible}
            style={{ width: '40vw', maxWidth: 880 }}
            breakpoints={{ '960px': '95vw' }}
            header={`${campaign?.name || 'Campaign'} — Analytics`}
            modal
            footer={footer}
            onHide={onClose}
            onShow={() => setChartReady(true)}  // render chart after dialog finishes its open animation
            className="analytics-detail-dialog"
        >
            {isEmpty ? (
                <div className="py-5 text-center text-muted">
                    <i className="fa-regular fa-chart-bar d-block mb-2" style={{ fontSize: 28 }} />
                    {t('devices.setup_no_analytics_media')}
                </div>
            ) : (
                <div className="p-5 w-100 flex flex-col justify-center items-center">
                    {/* Age distribution */}
                    <section className="panel card w-75 h-64">   {/* give the section a fixed height or inherit */}
                        <header className="panel__head">
                            <h6 className="panel__title">{t('devices.setup_age_distribution')}</h6>
                        </header>
                        <div className="flex-1 h-full">
                            {chartReady && (
                                <Chart
                                    type="bar"
                                    data={barData}
                                    options={barOptions}
                                    style={{ width: '100%', height: '100%' }}  // chart fills container
                                />
                            )}
                        </div>
                    </section>

                    {/* Metrics */}
                    <section className="w-75 my-3">
                        <div className="metrics">
                            <div className="flex w-100 justify-between items-center">
                                <div className="flex gap-3">
                                    <div className="">{t('devices.setup_media_name')}:</div>
                                    <div className="text-truncate">
                                        <i className="fa-solid fa-images me-2" />
                                        {campaign?.name || '-'}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="">{t('devices.setup_analytics_views')}:</div>
                                    <div className="">
                                        <i className="fa-regular fa-eye me-2" />
                                        {campaign?.views ?? 0}
                                    </div>
                                </div>
                            </div>

                            <div className="flex flex-col gap-2">
                                <div className="">{t('devices.setup_analytics_gender')}: </div>
                                <div className="w-100 flex-col flex items-center justify-center">
                                    <div className="stacked-bar flex items-center justify-center w-75 mb-1">
                                        <div className="stacked-bar__male" style={{ width: `${malePct}%` }} />
                                        <div className="stacked-bar__female" style={{ width: `${femalePct}%` }} />
                                    </div>
                                    <div className="w-100 flex flex-col justify-center">
                                        <div className="w-100 flex items-center justify-center small text-muted">
                                            <i className="fa-solid fa-person me-1" />
                                            {malePct}% ({male})
                                            <span className="mx-2">•</span>
                                            <i className="fa-solid fa-person-dress me-1" />
                                            {femalePct}% ({female})
                                        </div>
                                        <div className="w-100 flex items-center justify-center legend mt-2">
                                            <span className="legend__chip legend__chip--male">{t('devices.setup_gender_male')}</span>
                                            <span className="legend__chip legend__chip--female">{t('devices.setup_gender_female')}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            )}
        </Dialog>
    );
}

export default AnalyticsDetailDialog;
